import axios, { AxiosError } from 'axios';
import React from "react";
import config from '../../../../../../../../config';
import { filterLogPhotos } from '../../methods';
import "./PhotoEditor.scss";
//#region ICONS
import { Undo } from '@mui/icons-material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
//#endregion
//#region COMPONENTS
import GestButton from "@shared/library/dist/components/gest/GestInputs/button/GestButton";
import GestSelect from "@shared/library/dist/components/gest/GestInputs/select/GestSelect";
import { CustomDetails, EasyDropZone, ImageCard } from '../../../../../../../gest';
import { ImageData } from '../../../../../../../gest/CustomGallery/CustomGallery';
//#endregion
//#region MODELS
import { ISharedConfig, setDialog, setSnackbar } from '@shared/library';
import openGestConfirmationDialog from '@shared/library/dist/components/gest/GestDialog/GestConfirmationDialog/GestConfirmationDialog';
import { IGestSelectOption } from '@shared/library/dist/components/gest/GestInputs/select/GestSelectProps';
import { IIntervention } from '../../_models/Intervention';
import { OriginalPhoto, PhotoResponse } from '../InterventionManagement';
//#endregion

interface IPhotoUpload {
    file: File,
    dataURL: string,
    category?: string,
}

const PhotoEditor = (props: {
    config: ISharedConfig,
    lang: any;
    clickedIntervention?: IIntervention;
    photos: React.RefObject<PhotoResponse | undefined>;
    categories: string[];
    logID: number;
    setTransformedPhotos: React.Dispatch<React.SetStateAction<ImageData[][]>>;
}) => {
    //STATES
    const [uploadedPhoto, setUploadedPhoto] = React.useState<IPhotoUpload>();
    const deletePhotoLoading = React.useRef(false);

    //#region METHODS
    const uploadPhoto = (event: React.SubmitEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (props.clickedIntervention && uploadedPhoto?.category) {
            const formData = new FormData();
            formData.append("file", uploadedPhoto.file);

            axios.post(`${config.api.internal}/WFM/Intervention/${props.clickedIntervention.id}/Photo/${uploadedPhoto.category}/Upload`, formData, { withCredentials: true })
                .then(res => {
                    setUploadedPhoto(undefined);
                    const data = res.data;
                    setSnackbar(props.config, { message: "Upload della foto avvenuto con successo", severity: 'success' });

                    if (!props.photos.current?.[data.category]) {
                        let tmp: any = {};
                        tmp[data.category] = [data];

                        props.photos.current = structuredClone(tmp);
                    } else {
                        props.photos.current[data.category].push(data);
                    }
                })
                .catch((err: AxiosError) => {
                    console.error(err);
                    setSnackbar(props.config, { message: "Errore durante l'upload della foto", severity: 'error' });
                });
        }
        else if (!uploadedPhoto?.category) {
            setSnackbar(props.config, { message: `${props.lang.select_a_category}!`, severity: 'warning' });
            return;
        }
    };

    const deletePhoto = (photo: OriginalPhoto) => {
        if (photo) {
            deletePhotoLoading.current = true;
            axios.delete(`${config.api.internal}/WFM/Intervention/Photo/${photo.id}/Delete`, { withCredentials: true })
                .then(() => {
                    if (props.photos.current) {
                        const tmpCategory = props.photos.current[photo.category].filter(v => v.id !== photo.id);

                        let tmp = structuredClone(props.photos.current);
                        tmp[photo.category] = structuredClone(tmpCategory);

                        props.photos.current = tmp;
                    }
                    const tmp = filterLogPhotos(props.lang, props.logID, props.photos.current);
                    props.setTransformedPhotos(tmp);
                    setDialog(props.config);
                })
                .catch((err: AxiosError) => {
                    console.error(err);
                    setSnackbar(props.config, { message: "Errore durante cancellazione della foto", severity: "error" });
                })
                .finally(() => deletePhotoLoading.current = false);
        }
    };
    //#endregion

    return <div id="photoEditor">
        <CustomDetails
            class='uploadPhoto mb-2'
            title={props.lang.import}
            variant="add-on"
        >
            <form onSubmit={uploadPhoto}>
                <EasyDropZone
                    accept=".jpeg, .jpg, .png"
                    onChange={(files: File[]) => {
                        const reader = new FileReader();

                        reader.onload = event => event.target?.result && setUploadedPhoto({
                            ...uploadedPhoto,
                            file: files[0],
                            dataURL: event.target.result.toString()
                        });

                        reader.readAsDataURL(files[0]);
                    }}
                />

                <div id='photoPreview'>
                    <GestSelect
                        name='category'
                        label={props.lang.category}
                        value={uploadedPhoto?.category}
                        options={props.categories.map<IGestSelectOption<undefined>>(v => ({ value: v, label: props.lang[v] ?? v }))}
                        onchange={selected => selected[0].value && uploadedPhoto && setUploadedPhoto({ ...uploadedPhoto, category: selected[0].value.toString() })}
                        disabled={!uploadedPhoto?.dataURL}
                        fullw
                        required
                    />

                    <GestButton
                        tooltip={!uploadedPhoto?.dataURL ? props.lang.no_file_loaded : !uploadedPhoto?.category ? `Seleziona ${props.lang.category}` : undefined}
                        label={props.lang.cancel}
                        className='btn-danger'
                        icon={<Undo />}
                        onclick={() => setUploadedPhoto(undefined)}
                        disabled={!uploadedPhoto?.dataURL || !uploadedPhoto?.category}
                        fullw
                    />

                    <GestButton
                        tooltip={!uploadedPhoto?.dataURL ? props.lang.no_file_loaded : !uploadedPhoto?.category ? `Seleziona ${props.lang.category}` : undefined}
                        label={props.lang.import}
                        icon={<FileUploadRoundedIcon />}
                        disabled={!uploadedPhoto?.dataURL || !uploadedPhoto?.category || !uploadedPhoto.file}
                        fullw
                        submit
                    />

                    <ImageCard
                        variant="polaroid"
                        src={uploadedPhoto?.dataURL}
                        alt={props.lang.preview}
                        title={props.lang.preview}
                    />
                </div>
            </form>
        </CustomDetails>

        {props.photos.current && Object.keys(props.photos.current).map(k =>
            <CustomDetails
                class='mb-2'
                title={props.lang[k] ?? k}
                variant="section"
                isOpen
            >
                <div style={{ display: "flex", gap: "10px" }}>
                    {props.photos.current![k].map((photo) =>
                        <ImageCard
                            download
                            variant="polaroid"
                            src={photo.url}
                            alt={photo.category}
                            title={props.lang[photo.category] ?? photo.category}
                            actions={
                                <GestButton
                                    iconBtn
                                    color='error'
                                    icon={<DeleteForeverIcon />}
                                    onclick={async () => {
                                        const confirmed = await openGestConfirmationDialog(
                                            dialog => setDialog(props.config, dialog),
                                            props.lang,
                                            <ImageCard
                                                variant="polaroid"
                                                src={photo.url}
                                                alt={photo.category}
                                                title={props.lang[photo.category] ?? photo.category}
                                                imageStyle={{ maxWidth: 150 }}
                                            />,
                                            true,
                                            { label: props.lang.photo, content: <PhotoEditor {...props} /> }
                                        );

                                        if (confirmed) {
                                            deletePhoto(photo);
                                        }
                                    }}
                                />
                            }
                        />)}
                </div>
            </CustomDetails>
        )}
    </div >;
};
export default React.memo(PhotoEditor);